import {
  ISecureInformationRequestTemplate,
  useSecureInformationRequestTemplates,
} from '@commenda/api';
import { SecureInformationRequestFieldType } from '@prisma/client';
import { useEffect, useState } from 'react';
import { GroupProps } from '../Admin/types';

const useSecureInformationRequestGroups = ({
  secureInformationRequestTemplateId,
  agentFirmId,
}: {
  secureInformationRequestTemplateId: string | null;
  agentFirmId: string | null;
}) => {
  const {
    data: secureInformationRequestTemplateResponse,
    error: secureInformationRequestTemplateError,
  } = useSecureInformationRequestTemplates(agentFirmId);

  const [groups, setGroups] = useState<GroupProps[]>([
    {
      indexInRequest: 0,
      fields: [
        {
          headerText: '',
          indexInGroup: 0,
          isSecure: false,
          type: '' as SecureInformationRequestFieldType,
        },
      ],
      headerText: '',
    },
  ]);

  const consumeSecureInformationRequestTemplate = (
    template: ISecureInformationRequestTemplate
  ) => {
    // Convert template groups to the format needed for the form
    const formattedGroups = template.groups?.map((group) => ({
      headerText: group.headerText,
      indexInRequest: group.indexInRequest,
      fields: group.fields.map((field) => ({
        headerText: field.headerText,
        isSecure: field.isSecure,
        type: field.type,
        indexInGroup: field.indexInGroup,
        selectOptions: field.selectOptions || [],
        ...(field.tooltipText ? { tooltip: field.tooltipText } : {}),
      })),
    }));

    setGroups(formattedGroups);
  };

  const filteredGroupsForPreview = groups
    .filter((group) => group.headerText.trim() !== '')
    .map((group) => ({
      ...group,
      fields: group.fields.filter((field) => {
        if (!field.type || field.headerText.trim() === '') return false;

        if (
          field.type === SecureInformationRequestFieldType.SELECT ||
          field.type === SecureInformationRequestFieldType.MULTI_SELECT
        ) {
          return field.selectOptions && field.selectOptions.length > 0;
        }

        return true;
      }),
    }))
    .filter((group) => group.fields.length > 0);

  useEffect(() => {
    if (
      !secureInformationRequestTemplateResponse ||
      !secureInformationRequestTemplateId
    )
      return;

    const secureInformationRequestTemplate =
      secureInformationRequestTemplateResponse.secureInformationRequestTemplates.find(
        (template) => template.id === secureInformationRequestTemplateId
      );

    if (!secureInformationRequestTemplate) return;

    consumeSecureInformationRequestTemplate(secureInformationRequestTemplate);
  }, [
    secureInformationRequestTemplateResponse,
    secureInformationRequestTemplateId,
  ]);

  return {
    groups,
    setGroups,
    filteredGroupsForPreview,
    isLoading: !secureInformationRequestTemplateResponse,
    error: secureInformationRequestTemplateError,
  };
};

export { useSecureInformationRequestGroups };
